import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styled, { keyframes } from "styled-components";

gsap.registerPlugin(ScrollTrigger);

const stats = [
  {
    id: 1,
    value: "200+",
    label: "Products",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
    ),
  },
  {
    id: 2,
    value: "50+",
    label: "Brands",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    id: 3,
    value: "25+",
    label: "Years Experience",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    id: 4,
    value: "HP",
    label: "Authorized Partner",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 5,
    value: "24/7",
    label: "Support",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
      </svg>
    ),
  },
];

const StatsBar = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section.querySelectorAll(".stat-item"),
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <StatsSection ref={sectionRef}>
      <div className="container">
        {stats.map((stat, index) => (
          <div key={stat.id} className="stat-item">
            <div className="icon-wrapper">{stat.icon}</div>
            <div className="stat-content">
              <span className="value">{stat.value}</span>
              <span className="label">{stat.label}</span>
            </div>
            {index < stats.length - 1 && <div className="divider" />}
          </div>
        ))}
      </div>
    </StatsSection>
  );
};

export default StatsBar;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const StatsSection = styled.section`
  background: linear-gradient(135deg, var(--primary) 0%, #1a2550 100%);
  padding: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    animation: ${pulse} 3s ease-in-out infinite;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 40px 40px;
    position: relative;
    z-index: 1;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    justify-content: center;
    position: relative;

    .icon-wrapper {
      width: 50px;
      height: 50px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(255, 255, 255, 0.1);

      svg {
        width: 24px;
        height: 24px;
        stroke: var(--accent);
      }
    }

    .stat-content {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .value {
        font-family: Poppins;
        font-size: 28px;
        font-weight: 800;
        color: #fff;
        line-height: 1;
      }

      .label {
        font-family: Poppins;
        font-size: 13px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .divider {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 50px;
      background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    }
  }

  @media (max-width: 1024px) {
    .container {
      flex-wrap: wrap;
      gap: 30px;
      padding: 30px 20px;
    }

    .stat-item {
      flex: 0 0 calc(33.333% - 20px);
      justify-content: flex-start;

      .divider {
        display: none;
      }

      .stat-content .value {
        font-size: 24px;
      }
    }
  }

  @media (max-width: 640px) {
    .container {
      gap: 24px;
    }

    .stat-item {
      flex: 0 0 calc(50% - 12px);

      .icon-wrapper {
        width: 44px;
        height: 44px;

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .stat-content {
        .value {
          font-size: 20px;
        }

        .label {
          font-size: 11px;
        }
      }
    }
  }
`;
